import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { NotificationSort, NotificationWatch, NotificationWatchDetail } from './notifications.types';

@Injectable({ providedIn: 'root' })
export class NotificationsDataService {
    constructor(private http: HttpClient) { }

    getWatches(sort?: NotificationSort): Observable<NotificationWatch[]> {
        const queryString = sort
            ? `?sortField=${encodeURIComponent(sort.sortField)}&sortDesc=${sort.sortDesc}`
            : '';

        return this.http
            .get<NotificationWatch[]>(`/api/projectwatch${queryString}`)
            .pipe(
                map((watches) =>
                    (watches || []).map((watch) => ({
                        ...watch,
                        name: (watch.name || '').replace(/\s{2,}/g, ' '),
                        originalName: (watch.name || '').replace(/\s{2,}/g, ' '),
                        validated: true,
                        isEdited: false,
                        isDeleting: false
                    }))
                )
            );
    }

    getWatchDefinition(guid: string): Observable<any> {
        return this.http.get<any>(`/api/projectwatch/${guid}`);
    }

    getWatchSummary(summaryGuid: string, sort: NotificationSort, source?: string): Observable<unknown[]> {
        const params: any = {
            guid: summaryGuid,
            paging: JSON.stringify({ limit: 20, offset: 0 }),
            sorting: JSON.stringify({ sort_order: sort.sortField, sort_desc: sort.sortDesc })
        };

        if (source) {
            params.source = source;
        }

        return this.http.get<any[]>(`/api/projectsummary/${summaryGuid}`, { params }).pipe(
            map((payload) => {
                const root = payload?.[0] || {};
                return root.summaries || root.projectSummaries || root.items || [];
            })
        );
    }

    getWatchDetail(guid: string, sort: NotificationSort): Observable<NotificationWatchDetail> {
        return this.getWatchDefinition(guid).pipe(
            switchMap((watchDefinition) => {
                const watch = this.normalizeWatchDefinition(watchDefinition);
                const candidateSummaryGuids = [
                    watchDefinition?.guid,
                    watch?.guid,
                    watchDefinition?.queryDef?.guid,
                    watchDefinition?.resubmitParameters?.guid,
                    guid
                ].filter((item, index, all) => !!item && all.indexOf(item) === index) as string[];

                const candidateSources = [
                    undefined,
                    'projectwatch',
                    watchDefinition?.resubmitParameters?.source
                ].filter((item, index, all) => all.indexOf(item) === index);

                return this.getWatchSummaryWithFallback(candidateSummaryGuids, candidateSources as (string | undefined)[], sort).pipe(
                    map((projects) => ({
                        watch,
                        projects,
                        highlightSinceDate: watchDefinition?.highlightSinceDate,
                        newSince: watchDefinition?.lastNotified || watchDefinition?.created
                    }))
                );
            })
        );
    }

    createWatch(payload: unknown): Observable<NotificationWatch> {
        // Legacy endpoint never returns a JSON body (200 + custom reason phrase only); skip JSON parsing entirely.
        return this.http
            .post('/api/projectwatch', payload, { responseType: 'text' })
            .pipe(map(() => undefined as unknown as NotificationWatch));
    }

    updateWatchProperties(guid: string, patch: unknown[]): Observable<NotificationWatch> {
        return this.http.post<NotificationWatch>(`/api/projectwatch/properties/${guid}`, patch, {
            headers: {
                'Content-Type': 'application/json-patch+json',
                'X-HTTP-Method-Override': 'PATCH'
            }
        });
    }

    updateWatch(watch: NotificationWatch): Observable<NotificationWatch> {
        const nextNotification = this.calculateNextNotification(watch);

        const patch = [
            { op: 'replace', path: '/WatchName', value: watch.name },
            { op: 'replace', path: '/Description', value: watch.description || '' },
            { op: 'replace', path: '/NextNotification', value: nextNotification },
            { op: 'replace', path: '/NotificationFrequency', value: this.translateNotificationFrequency(watch.notificationFrequency) }
        ];

        return this.updateWatchProperties(watch.guid, patch);
    }

    enableWatch(watch: NotificationWatch): Observable<NotificationWatch> {
        const patch = [
            { op: 'replace', path: '/isEnabled', value: !!watch.isEnabled },
            { op: 'replace', path: '/NextNotification', value: this.calculateNextNotification(watch) }
        ];

        return this.updateWatchProperties(watch.guid, patch);
    }

    deleteWatch(guid: string): Observable<void> {
        return this.http.delete<void>(`/api/projectwatch/${guid}`);
    }

    stopWatchingProjects(watchGuid: string, projectIDs: number[]): Observable<void> {
        return this.http.post<void>(`/api/projectwatch/projects/${watchGuid}?operation=removeProjects`, projectIDs, {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                'X-HTTP-Method-Override': 'PATCH'
            }
        });
    }

    restoreProject(watchGuid: string, projectID: number): Observable<void> {
        return this.http.post<void>(`/api/projectwatch/projects/${watchGuid}?operation=restoreProject`, [projectID], {
            headers: {
                'Content-Type': 'application/json;charset=utf-8',
                'X-HTTP-Method-Override': 'PATCH'
            }
        });
    }

    calculateNextNotification(watch: NotificationWatch): string | null {
        if (!watch.isEnabled) {
            return null;
        }

        const today = new Date(Date.now());

        if (watch.notificationFrequency === 'Daily') {
            return today.toISOString();
        }

        const todaysDayNumber = today.getDay();
        const nextSaturday = new Date(today.setDate(today.getDate() + (6 - todaysDayNumber)));
        return nextSaturday.toISOString();
    }

    private translateNotificationFrequency(frequency: string | undefined): number {
        if (frequency === 'Daily') {
            return 1;
        }

        return 7;
    }

    private normalizeWatchDefinition(watchDefinition: any): NotificationWatch {
        return {
            guid: watchDefinition?.guid || watchDefinition?.watchGuid || '',
            name: watchDefinition?.name || watchDefinition?.watchName || watchDefinition?.WatchName || '',
            description: watchDefinition?.description || watchDefinition?.Description,
            notificationFrequency:
                watchDefinition?.notificationFrequency ||
                (watchDefinition?.NotificationFrequency === 1 ? 'Daily' : 'Weekly') ||
                'Weekly',
            watchType: watchDefinition?.watchType || watchDefinition?.WatchType,
            projectIDs: watchDefinition?.projectIDs || watchDefinition?.ProjectIDs || [],
            isEnabled: watchDefinition?.isEnabled ?? watchDefinition?.IsEnabled,
            nextNotification: watchDefinition?.nextNotification || watchDefinition?.NextNotification,
            lastNotified: watchDefinition?.lastNotified || watchDefinition?.LastNotified,
            created: watchDefinition?.created || watchDefinition?.Created,
            validated: true,
            isEdited: false,
            isDeleting: false,
            originalName: watchDefinition?.name || watchDefinition?.watchName || watchDefinition?.WatchName || ''
        };
    }

    nameDuplicated(watches: NotificationWatch[], watchName: string): boolean {
        const sameNameCount = (watches || []).filter(
            (watch) => (watch.name || '').toLowerCase() === (watchName || '').toLowerCase()
        ).length;

        return sameNameCount > 1;
    }

    validateWatches(watches: NotificationWatch[]): boolean {
        let allValid = true;

        (watches || []).forEach((watch) => {
            watch.name = (watch.name || '').replace(/\s{2,}/g, ' ').trim();
            const missingName = !watch.name;
            const duplicate = this.nameDuplicated(watches, watch.name);
            watch.validated = !missingName && !duplicate;
            allValid = allValid && !!watch.validated;
        });

        return allValid;
    }

    private getWatchSummaryWithFallback(
        summaryGuids: string[],
        sources: (string | undefined)[],
        sort: NotificationSort
    ): Observable<unknown[]> {
        const candidates: Array<{ guid: string; source?: string }> = [];

        summaryGuids.forEach((candidateGuid) => {
            sources.forEach((candidateSource) => {
                candidates.push({ guid: candidateGuid, source: candidateSource });
            });
        });

        const tryNext = (index: number, firstResult: unknown[] = []): Observable<unknown[]> => {
            if (index >= candidates.length) {
                return of(firstResult);
            }

            const candidate = candidates[index];
            return this.getWatchSummary(candidate.guid, sort, candidate.source).pipe(
                catchError(() => of([])),
                switchMap((items) => {
                    if (items.length > 0) {
                        return of(items);
                    }

                    const rememberedFirst = firstResult.length ? firstResult : items;
                    return tryNext(index + 1, rememberedFirst);
                })
            );
        };

        return tryNext(0);
    }
}
